import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { lastValueFrom, map } from 'rxjs';
import { FinancialControllerService } from 'src/app/admin-api/api/financialController.service';
import { PixPaymentReport } from 'src/app/admin-api/model/pixPaymentReport';
import { TableColumn } from 'src/app/components/table';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';

interface PixPaymentReportRow {
  approvalRatePercent: number;
  dateScheduled: Date;
  total: number;
  totalFailed: number;
  totalPaid: number;
  totalScheduled: number;
}

@Component({
  selector: 'app-scheduler-jobs-pix-report',
  templateUrl: './scheduler-jobs-pix-report.component.html',
  styleUrls: ['./scheduler-jobs-pix-report.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class SchedulerJobsPixReportComponent implements OnInit {
  private financialService = inject(FinancialControllerService);
  private activatedRoute = inject(ActivatedRoute);
  private router = inject(Router);

  private static readonly REFERENCE_MONTH_PARAM = 'referenceMonth';

  selectedMonthLabel = '';

  monthFilterForm = new FormGroup({
    month: new FormControl<Date>(new Date(), Validators.required)
  });

  kpiTotalScheduled = 0;
  kpiTotalPaid = 0;
  kpiTotalFailed = 0;
  kpiApprovalRate = 0;

  pixReportRows: PixPaymentReportRow[] = [];
  pixReportCols: TableColumn[] = [
    new TableColumn.Builder()
      .setHeader('Data')
      .setField('dateScheduled')
      .setType('date')
      .setCondition('between')
      .build(),
    new TableColumn.Builder()
      .setHeader('Agendadas')
      .setField('totalScheduled')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Pagas')
      .setField('totalPaid')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Falhas')
      .setField('totalFailed')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Total')
      .setField('total')
      .setType('number')
      .setCondition('equals')
      .build(),
    new TableColumn.Builder()
      .setHeader('Aprovacao')
      .setField('approvalRatePercent')
      .setDisplayFunction(
        (row: PixPaymentReportRow) => `${row.approvalRatePercent.toFixed(2)}%`
      )
      .setCondition('between')
      .build()
  ];

  chartData: {
    labels: string[];
    datasets: {
      type: 'line' | 'bar';
      label: string;
      data: number[];
      yAxisID: string;
      stack?: string;
      borderColor?: string;
      backgroundColor?: string;
      borderWidth?: number;
      tension?: number;
    }[];
  } = {
    labels: [],
    datasets: []
  };

  chartOptions = {
    maintainAspectRatio: false,
    responsive: true,
    interaction: {
      mode: 'index',
      intersect: false
    },
    scales: {
      y: {
        type: 'linear',
        display: true,
        position: 'left',
        title: {
          display: true,
          text: 'Volumes'
        },
        beginAtZero: true
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        title: {
          display: true,
          text: 'Aprovacao (%)'
        },
        min: 0,
        max: 100,
        grid: {
          drawOnChartArea: false
        }
      }
    }
  };

  async ngOnInit(): Promise<void> {
    LoaderService.showLoader();
    try {
      this.syncMonthFromQueryParam();
      await this.syncReferenceMonthQueryParam();
      await this.loadReportBySelectedMonthData();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  async goToPreviousMonth(): Promise<void> {
    const currentMonth = this.monthFilterForm.controls.month.value;
    if (!currentMonth) {
      return;
    }

    const previousMonth = new Date(currentMonth);
    previousMonth.setMonth(previousMonth.getMonth() - 1);
    this.monthFilterForm.controls.month.setValue(previousMonth);
    await this.loadReportBySelectedMonth();
  }

  async goToNextMonth(): Promise<void> {
    const currentMonth = this.monthFilterForm.controls.month.value;
    if (!currentMonth) {
      return;
    }

    const nextMonth = new Date(currentMonth);
    nextMonth.setMonth(nextMonth.getMonth() + 1);
    this.monthFilterForm.controls.month.setValue(nextMonth);
    await this.loadReportBySelectedMonth();
  }

  async loadReportBySelectedMonth(): Promise<void> {
    LoaderService.showLoader();
    try {
      await this.syncReferenceMonthQueryParam();
      await this.loadReportBySelectedMonthData();
    } catch (error) {
      AppDialogService.showErrorDialog(error);
    } finally {
      LoaderService.showLoader(false);
    }
  }

  private async loadReportBySelectedMonthData(): Promise<void> {
    const selectedMonth = this.monthFilterForm.controls.month.value;
    if (!selectedMonth) {
      return;
    }

    const startDate = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth(),
      1,
      0,
      0,
      0,
      0
    );
    const endDate = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0,
      23,
      59,
      59,
      999
    );

    const response = await lastValueFrom(
      this.financialService
        .getPixPaymentReportByDateRange(startDate, endDate)
        .pipe(map((data) => data.result || []))
    );

    this.selectedMonthLabel = new Intl.DateTimeFormat('pt-BR', {
      month: 'long',
      year: 'numeric'
    }).format(startDate);

    this.pixReportRows = this.mapRows(response);
    this.rebuildKpis();
    this.rebuildChart();
  }

  private mapRows(items: PixPaymentReport[]): PixPaymentReportRow[] {
    return (items || [])
      .map((item) => {
        const totalScheduled = Number(item.totalScheduled ?? 0);
        const totalPaid = Number(item.totalPaid ?? 0);
        const totalFailed = Number(item.totalFailed ?? 0);
        const total = Number(item.total ?? totalScheduled + totalFailed);
        const approvalRateRaw = Number(item.approvalRate ?? 0);
        const approvalRatePercent =
          approvalRateRaw <= 1 ? approvalRateRaw * 100 : approvalRateRaw;

        return {
          approvalRatePercent,
          dateScheduled: new Date(item.dateScheduled as string | Date),
          total,
          totalFailed,
          totalPaid,
          totalScheduled
        } as PixPaymentReportRow;
      })
      .sort(
        (a, b) =>
          new Date(a.dateScheduled).getTime() -
          new Date(b.dateScheduled).getTime()
      );
  }

  private rebuildKpis(): void {
    this.kpiTotalScheduled = this.pixReportRows.reduce(
      (sum, row) => sum + row.totalScheduled,
      0
    );
    this.kpiTotalPaid = this.pixReportRows.reduce(
      (sum, row) => sum + row.totalPaid,
      0
    );
    this.kpiTotalFailed = this.pixReportRows.reduce(
      (sum, row) => sum + row.totalFailed,
      0
    );

    this.kpiApprovalRate =
      (this.kpiTotalPaid / (this.kpiTotalPaid + this.kpiTotalFailed)) * 100;
  }

  private rebuildChart(): void {
    const labels = this.pixReportRows.map((row) =>
      row.dateScheduled.toLocaleDateString('pt-BR')
    );

    this.chartData = {
      labels,
      datasets: [
        {
          type: 'bar',
          label: 'Total agendadas',
          yAxisID: 'y',
          stack: 'pix-scheduled',
          data: this.pixReportRows.map((row) => row.totalScheduled),
          backgroundColor: '#93c5fd',
          borderColor: '#3b82f6',
          borderWidth: 1
        },
        {
          type: 'bar',
          label: 'Total pagas',
          yAxisID: 'y',
          stack: 'pix-breakdown',
          data: this.pixReportRows.map((row) => row.totalPaid),
          backgroundColor: '#22c55e',
          borderColor: '#16a34a',
          borderWidth: 1
        },
        {
          type: 'bar',
          label: 'Total falhas',
          yAxisID: 'y',
          stack: 'pix-breakdown',
          data: this.pixReportRows.map((row) => row.totalFailed),
          backgroundColor: '#f97316',
          borderColor: '#ea580c',
          borderWidth: 1
        },
        {
          type: 'bar',
          label: 'Total',
          yAxisID: 'y',
          stack: 'pix-total',
          data: this.pixReportRows.map((row) => row.total),
          backgroundColor: '#111827',
          borderColor: '#000000',
          borderWidth: 1
        },
        {
          type: 'line',
          label: 'Aprovacao diaria (%)',
          yAxisID: 'y1',
          data: this.pixReportRows.map((row) => row.approvalRatePercent),
          borderColor: '#16a34a',
          backgroundColor: '#16a34a',
          borderWidth: 2,
          tension: 0.25
        }
      ]
    };
  }

  private syncMonthFromQueryParam(): void {
    const referenceMonth = this.activatedRoute.snapshot.queryParamMap.get(
      SchedulerJobsPixReportComponent.REFERENCE_MONTH_PARAM
    );
    const parsedMonth = this.parseReferenceMonth(referenceMonth);
    if (!parsedMonth) {
      return;
    }

    this.monthFilterForm.controls.month.setValue(parsedMonth);
  }

  private async syncReferenceMonthQueryParam(): Promise<void> {
    const selectedMonth = this.monthFilterForm.controls.month.value;
    if (!selectedMonth) {
      return;
    }

    const referenceMonth = this.formatReferenceMonth(selectedMonth);
    const currentReferenceMonth =
      this.activatedRoute.snapshot.queryParamMap.get(
        SchedulerJobsPixReportComponent.REFERENCE_MONTH_PARAM
      );

    if (currentReferenceMonth === referenceMonth) {
      return;
    }

    await this.router.navigate([], {
      relativeTo: this.activatedRoute,
      queryParams: {
        [SchedulerJobsPixReportComponent.REFERENCE_MONTH_PARAM]: referenceMonth
      },
      queryParamsHandling: 'merge'
    });
  }

  private parseReferenceMonth(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const normalized = value.trim();
    const isoMatch = /^(\d{4})-(\d{2})$/.exec(normalized);
    if (isoMatch) {
      const year = Number(isoMatch[1]);
      const month = Number(isoMatch[2]) - 1;
      if (month >= 0 && month <= 11) {
        return new Date(year, month, 1, 0, 0, 0, 0);
      }
      return null;
    }

    const brMatch = /^(\d{2})\/(\d{4})$/.exec(normalized);
    if (brMatch) {
      const month = Number(brMatch[1]) - 1;
      const year = Number(brMatch[2]);
      if (month >= 0 && month <= 11) {
        return new Date(year, month, 1, 0, 0, 0, 0);
      }
      return null;
    }

    return null;
  }

  private formatReferenceMonth(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }
}
